/**
 * ContractCard Component - T165
 *
 * Card display for contract list items
 */
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { GlassPanel } from '../ui/GlassPanel';
import { ContractStatusBadge } from './ContractStatusBadge';
import type { Contract } from '../../hooks/useContracts';
import { billingIntervalLabels } from '../../hooks/useContracts';
import { formatCurrency, formatDate } from '@armard/shared';

interface ContractCardProps {
  contract: Contract;
  compact?: boolean;
}

export function ContractCard({ contract, compact = false }: ContractCardProps) {
  // Calculate monthly value
  const items = contract.items || [];
  const totalValue = items.reduce((sum, item) => {
    return sum + parseFloat(item.quantity) * parseFloat(item.unitPrice);
  }, 0);
  const vatRate = parseFloat(contract.vatRate);
  const grossTotal = totalValue * (1 + vatRate / 100);

  if (compact) {
    return (
      <Link to="/contracts/$id" params={{ id: contract.id }}>
        <motion.div whileTap={{ scale: 0.98 }} className="block">
          <GlassPanel hover className="transition-all duration-200 active:bg-white/5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white truncate">{contract.contractNumber}</span>
                  <ContractStatusBadge status={contract.status} size="sm" />
                </div>
                <p className="text-sm text-white/50 truncate mt-0.5">
                  {contract.customer?.companyName || 'Kunde'}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold text-white">{formatCurrency(grossTotal)}</p>
                <p className="text-xs text-white/40">{billingIntervalLabels[contract.billingInterval]}</p>
              </div>
              <svg className="w-4 h-4 text-white/30 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </GlassPanel>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to="/contracts/$id" params={{ id: contract.id }}>
      <motion.div whileTap={{ scale: 0.98 }} className="block h-full">
        <GlassPanel hover className="h-full transition-all duration-200 hover:border-white/20 active:bg-white/5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-white truncate">{contract.title}</h3>
              <p className="text-sm text-white/50 truncate">{contract.contractNumber}</p>
            </div>
            <ContractStatusBadge status={contract.status} />
          </div>

          {/* Customer */}
          <p className="text-sm text-white/60 mb-3">{contract.customer?.companyName || 'Kunde'}</p>

          {/* Dates and Billing */}
          <div className="flex flex-col gap-1 mb-3 text-sm">
            <div className="flex justify-between">
              <span className="text-white/40">Laufzeit:</span>
              <span className="text-white/70">
                {formatDate(contract.startDate)}
                {contract.endDate ? ` - ${formatDate(contract.endDate)}` : ' - unbefristet'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/40">Abrechnung:</span>
              <span className="text-white/70">{billingIntervalLabels[contract.billingInterval]}</span>
            </div>
            {contract.nextBillingDate && (
              <div className="flex justify-between">
                <span className="text-white/40">Nächste Rechnung:</span>
                <span className="text-white/70">{formatDate(contract.nextBillingDate)}</span>
              </div>
            )}
          </div>

          {/* Value */}
          <div className="flex items-end justify-between pt-3 border-t border-white/5">
            <div className="text-sm text-white/40">
              {items.length} Position{items.length !== 1 ? 'en' : ''}
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold text-white">{formatCurrency(grossTotal)}</div>
              <div className="text-xs text-white/40">pro Periode</div>
            </div>
          </div>
        </GlassPanel>
      </motion.div>
    </Link>
  );
}
